import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OfferStatusBadge } from "./OfferStatusBadge";
import { OfferProbabilityBadge } from "./OfferProbabilityBadge";
import { OfferPipelineControls } from "./OfferPipelineControls";
import { OfferEditMenu } from "./OfferEditMenu";
import { OfferEditorsPicker } from "./OfferEditorsPicker";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import type { Offer, OfferStatus, OfferProbability } from "@/hooks/use-offers";

interface Props {
  offer: Offer;
  customerLabel: string;
  draftTitle: string;
  /**
   * Read-only-modus (ikke-forfatter/ikke-admin). Skjuler/deaktiverer alle
   * redigerings-affordanser (tittel, lagre, konverter, status, slett,
   * innstillinger). Eksport (Keynote) og "Gå til prosjekt" forblir.
   * VIB-offer-author-gating.
   */
  readOnly?: boolean;
  canManage?: boolean;
  canSave: boolean;
  isSaving: boolean;
  isExportingPptx: boolean;
  hasLines: boolean;
  hasProject: boolean;
  onTitleChange: (next: string) => void;
  onSave: () => void;
  onStatusChange: (status: OfferStatus) => void;
  onConvertRequest: () => void;
  onDelete: () => void;
  onExportPptx: () => void;
  onDuplicate?: () => void;
  isDuplicating?: boolean;
  onOpenSettings: () => void;
  onOpenKeyboardHelp?: () => void;
  /**
   * VIB-1304, bak feature_offer_pipeline (leses av kalleren via
   * useFeatureFlag). Når false vises verken kategori-badge eller kontroller
   * — pakken er helt usynlig til Erik ruller ut flagget.
   */
  pipelineEnabled?: boolean;
  onProbabilityChange?: (p: OfferProbability) => void;
  onValidUntilChange?: (date: string | null) => void;
}

/**
 * Kompakt header for tilbud-editoren. Tilbake-lenke, redigerbart
 * tilbudsnavn, status-badge, lagre-knapp + meny på én rad. ⌘L-katalog-
 * søk håndteres på side-nivå (VIB-700). Under `md` rendres en enda
 * tettere mobilrad (VIB-703).
 */
export function OfferEditHeader({
  offer,
  customerLabel,
  draftTitle,
  readOnly = false,
  canManage = !readOnly,
  canSave,
  isSaving,
  isExportingPptx,
  hasLines,
  hasProject,
  onTitleChange,
  onSave,
  onStatusChange,
  onConvertRequest,
  onDelete,
  onExportPptx,
  onDuplicate,
  isDuplicating = false,
  onOpenSettings,
  onOpenKeyboardHelp,
  pipelineEnabled = false,
  onProbabilityChange,
  onValidUntilChange,
}: Props) {
  const renderMenuContent = () => (
    <OfferEditMenu
      offer={offer}
      hasLines={hasLines}
      hasProject={hasProject}
      isExportingPptx={isExportingPptx}
      isDuplicating={isDuplicating}
      canSave={canSave}
      readOnly={readOnly}
      canManage={canManage}
      onStatusChange={onStatusChange}
      onDelete={onDelete}
      onExportPptx={onExportPptx}
      onOpenSettings={onOpenSettings}
      onDuplicate={onDuplicate}
    />
  );

  return (
    <div className="border rounded-md bg-background mb-2 sticky top-0 z-30 shadow-sm">
      {/* Mobile compact header (under md): én rad med tilbake, tittel,
          status, lagre-ikon og meny. Kunde og "Konverter" er ikke
          prioritert på mobil — Konverter kan tas fra desktop. */}
      <div className="md:hidden flex items-center gap-2 px-3 py-2">
        <Button asChild variant="ghost" size="icon" className="h-8 w-8 -ml-1">
          <Link to="/tilbud" aria-label="Tilbake">
            <SrIcon name="arrow-left" className="w-4 h-4" />
          </Link>
        </Button>
        <Input
          value={draftTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          readOnly={readOnly}
          placeholder="Tilbudstittel"
          className={cn(
            "flex-1 min-w-0 h-8 px-2 text-sm",
            "border-transparent shadow-none bg-transparent",
            "focus-visible:bg-background focus-visible:border-border",
          )}
        />
        <OfferStatusBadge status={offer.status} />
        {pipelineEnabled && <OfferProbabilityBadge probability={offer.probability} />}
        {!readOnly && (
          <Button
            onClick={onSave}
            disabled={!canSave || isSaving}
            size="icon"
            className="h-8 w-8"
            aria-label="Lagre"
          >
            {isSaving ? (
              <SrIcon name="loader2" className="w-4 h-4 animate-spin" />
            ) : (
              <SrIcon name="save" className="w-4 h-4" />
            )}
          </Button>
        )}
        {onOpenKeyboardHelp && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onOpenKeyboardHelp}
            aria-label="Tastatursnarveier"
          >
            <span className="text-xs font-mono">?</span>
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Mer">
              <SrIcon name="more-horizontal" className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          {renderMenuContent()}
        </DropdownMenu>
      </div>

      {/* Desktop: én kompakt rad — tittel og status til venstre, lagre til høyre. */}
      <div className="hidden md:flex items-center gap-2 px-3 py-1.5 flex-wrap">
        <Button asChild variant="ghost" size="sm" className="h-7 -ml-1 px-2">
          <Link to="/tilbud">
            <SrIcon name="arrow-left" className="w-3.5 h-3.5 mr-1" />
            Tilbake
          </Link>
        </Button>

        <Input
          value={draftTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          readOnly={readOnly}
          placeholder="Tilbudstittel"
          className={cn(
            "font-display text-base h-7 px-2 flex-1 min-w-[160px] max-w-[360px]",
            "border-transparent shadow-none bg-transparent",
            "focus-visible:bg-background focus-visible:border-border",
          )}
        />

        <OfferStatusBadge status={offer.status} />

        {pipelineEnabled &&
          (readOnly || !onProbabilityChange || !onValidUntilChange ? (
            <OfferProbabilityBadge probability={offer.probability} />
          ) : (
            <OfferPipelineControls
              probability={offer.probability}
              validUntil={offer.valid_until}
              readOnly={readOnly}
              onProbabilityChange={onProbabilityChange}
              onValidUntilChange={onValidUntilChange}
            />
          ))}

        <span className="text-xs text-muted-foreground truncate max-w-[220px]">
          {customerLabel}
        </span>
        <OfferEditorsPicker
          offerId={offer.id}
          ownerId={offer.created_by}
          canManage={canManage}
        />

        <div className="ml-auto flex items-center gap-2">
          {!readOnly && (
            <>
              <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                <span
                  className={cn(
                    "w-1.5 h-1.5 rounded-full",
                    canSave ? "bg-amber-500" : "bg-success",
                  )}
                  aria-hidden
                />
                {canSave ? "Ulagret" : "Lagret"}
              </span>

              <Button
                onClick={onSave}
                disabled={!canSave || isSaving}
                size="sm"
                className="h-7"
              >
                {isSaving ? (
                  <SrIcon name="loader2" className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <SrIcon name="save" className="w-3.5 h-3.5 mr-1" />
                )}
                Lagre
                <span className="ml-1.5 text-[10px] opacity-60 font-mono">⌘S</span>
              </Button>

              {canManage && offer.status === "accepted" && !hasProject && (
                <Button
                  onClick={onConvertRequest}
                  disabled={canSave}
                  variant="default"
                  size="sm"
                  className="h-7 bg-success hover:bg-success/90 text-success-foreground"
                  title={canSave ? "Lagre endringer først" : "Opprett prosjekt fra dette tilbudet"}
                >
                  <SrIcon name="arrow-right" className="w-3.5 h-3.5 mr-1" />
                  Konverter til prosjekt
                </Button>
              )}
            </>
          )}

          {onOpenKeyboardHelp && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={onOpenKeyboardHelp}
              aria-label="Tastatursnarveier"
              title="Tastatursnarveier (?)"
            >
              <span className="text-xs font-mono">?</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 px-2" aria-label="Mer">
                <SrIcon name="more-horizontal" className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            {renderMenuContent()}
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
